import { getWorkosAccessToken } from '../models/useAuth'

const routerApiBaseUrl =
  import.meta.env.VITE_ROUTER_API_URL?.replace(/\/$/, '') ?? 'https://router.nobgp.com'

export type ToolError = {
  code: string
  message: string
  /** M-17's correlation id, stamped on the FAILURE as well as on the success envelope. */
  call_id?: string
  retryable?: boolean
  details?: unknown
}

/**
 * What `callTool` throws. An `Error`, so every existing `catch` keeps working, plus the two
 * fields the envelope already carried and this client used to drop on the floor (app#200).
 *
 * ⚠⚠ THE CODE WAS THROWN AWAY, AND THAT IS WHY THE BILLING PANEL RENDERED A GO ERROR. `message`
 * was the only thing that survived the `throw`, so the one surface where a click moves money had
 * nothing to map on and printed the raw text — a SQL function name, an org id and a driver's
 * wording — in front of a customer. `utilities/billingError.ts` turns these two fields into a
 * sentence; nothing else reads them yet, and nothing else has to.
 *
 * ⚠ `callId` is the router's `call_id` and is the handle support can quote. It is present on
 * every ToolError the router builds (`Tool.Invoke` stamps the failure with the same id the result
 * envelope carries), and absent on a failure that never reached a handler.
 *
 * ⚠⚠ `details` WAS DECLARED ON `ToolError` AND DROPPED HERE, WHICH COST THE ONE REFUSAL THAT
 * SHIPS A REMEDY (app#296). The router's `limitError` (`src/tools/limits.go`) attaches
 * `limit_type`, `used`, `included`, `hard_cap` and `upgrade_url` to a creation cap precisely so
 * this app can offer an Upgrade action, and its own comment says the app renders it — but this
 * constructor took four fields and left the fifth on the floor, so a customer refused for a spent
 * allowance got the sentence and no way to act on it. It is `unknown` because the payload is
 * per-tool: `utilities/limitRefusal.ts` reads the creation-cap shape, and nothing else reads it.
 */
export class ToolCallError extends Error {
  readonly code: string
  readonly callId: string
  readonly retryable: boolean
  readonly status: number
  readonly details: unknown

  constructor(
    message: string,
    init: { code?: string; callId?: string; retryable?: boolean; status?: number; details?: unknown } = {}
  ) {
    super(message)
    this.name = 'ToolCallError'
    this.code = init.code ?? ''
    this.callId = init.callId ?? ''
    this.retryable = init.retryable ?? false
    this.status = init.status ?? 0
    this.details = init.details
  }
}

/**
 * Calls a router tool (POST /api/v1/tools/{name}) with the WorkOS bearer token.
 *
 * Role-restricted writes (org/member/network create-delete, billing, …) must go
 * through here rather than a direct Supabase upsert: RLS isolates by org
 * membership but does not encode the role matrix — that is the router's
 * requireRole gate (Epic 1.2). On success the body is the tool's typed result;
 * on failure it is a ToolError envelope.
 */
export async function callTool<T = unknown>(
  name: string,
  args: Record<string, unknown> = {}
): Promise<T> {
  return unwrapToolPayload(name, await callToolEnvelope(name, args)) as T
}

/**
 * Calls a router tool like `callTool`, but returns the WHOLE result envelope, not the payload.
 *
 * ⚠ Use it only where the page must read an envelope field, for example the router's `notes`
 * beside the payload (the node access list's note that a node offers only root, app#375). Every
 * other caller uses `callTool`, which unwraps.
 */
export async function callToolEnvelope(name: string, args: Record<string, unknown> = {}): Promise<unknown> {
  const token = getWorkosAccessToken()
  // ⚠ A code of its own, because this refusal never leaves the browser: it is the one failure on
  // the money path we can say with certainty charged nothing (app#200).
  if (!token) throw new ToolCallError('Not authenticated', { code: 'unauthenticated' })

  const response = await fetch(`${routerApiBaseUrl}/api/v1/tools/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(args),
  })

  const body = await response.json().catch(() => null)
  if (!response.ok) {
    const err = body as ToolError | null
    throw new ToolCallError(err?.message || `Tool ${name} failed (${response.status})`, {
      code: err?.code,
      callId: err?.call_id,
      retryable: err?.retryable,
      status: response.status,
      details: err?.details,
    })
  }
  return body
}

/**
 * Returns a tool's payload from its response body.
 *
 * ⚠⚠ THE ROUTER NESTS EVERY PAYLOAD UNDER THE TOOL'S OWN NAME, and has since
 * `2f6ae3f5` ("one result envelope across the whole tool surface", 2026-08-19).
 * A body is
 *
 *     {"call_id":"…","op":"…","done":true,"duration_ms":3,
 *      "org_members_list":{"members":[…]}}
 *
 * and every caller in this app was written against the FLAT shape that commit
 * replaced — `res.members`, `res.portal_url`, `res.checkout_url`. All of them
 * had been reading `undefined` since, and the `?? []` guards are why an empty
 * member list looked like an org with one member rather than a failed read
 * (app#173).
 *
 * Unwrapping here rather than at each call site is deliberate: the next tool
 * this app calls is correct without anyone remembering the rule.
 *
 * ⚠ A body with no payload object under that key is returned UNCHANGED. Some
 * tools answer with the envelope alone, and a caller reading an envelope field
 * must keep working.
 */
export const unwrapToolPayload = (name: string, body: unknown): unknown => {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return body
  const payload = (body as Record<string, unknown>)[name]
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return body
  return payload
}
